class PrintLoader extends HTMLElement {
  constructor() {
    super();

    this.items = [];
    this.positions = ["front", "back"];
    this.labels = {
      front: "Frente",
      back: "Verso",
    };
    this.propertyInputs = {};
  }

  connectedCallback() {
    this.maxFiles = Number(this.dataset.maxFiles || 2);
    this.maxSize = Number(this.dataset.maxSize || 2097152);
    this.list = this.querySelector("[data-print-loader-list]");
    this.dropzone = this.querySelector("[data-print-loader-dropzone]");
    this.picker = this.querySelector("[data-print-loader-picker]");
    this.trigger = this.querySelector("[data-print-loader-trigger]");
    this.error = this.querySelector("[data-print-loader-error]");
    this.propertyInputs.front = this.querySelector('[data-print-loader-input="front"]');
    this.propertyInputs.back = this.querySelector('[data-print-loader-input="back"]');

    this.form = this.getAssociatedForm();
    this.bindEvents();
    this.render();
  }

  disconnectedCallback() {
    this.items.forEach((item) => URL.revokeObjectURL(item.preview));
  }

  getAssociatedForm() {
    const input = this.propertyInputs.front || this.propertyInputs.back;
    if (input?.form) return input.form;
    return this.closest("form");
  }

  bindEvents() {
    this.trigger?.addEventListener("click", (event) => {
      event.stopPropagation();
      this.picker?.click();
    });
    this.dropzone?.addEventListener("click", (event) => {
      if (event.target === this.picker) return;
      this.picker?.click();
    });

    this.picker?.addEventListener("change", (event) => {
      this.addFiles(Array.from(event.target.files || []));
      event.target.value = "";
    });

    ["dragenter", "dragover"].forEach((eventName) => {
      this.dropzone?.addEventListener(eventName, (event) => {
        event.preventDefault();
        this.dropzone.classList.add("is-dragover");
      });
    });

    ["dragleave", "drop"].forEach((eventName) => {
      this.dropzone?.addEventListener(eventName, (event) => {
        event.preventDefault();
        this.dropzone.classList.remove("is-dragover");
      });
    });

    this.dropzone?.addEventListener("drop", (event) => {
      this.addFiles(Array.from(event.dataTransfer?.files || []));
    });

    this.form?.addEventListener("submit", (event) => {
      if (!this.validateBeforeSubmit()) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }, true);
  }

  addFiles(files) {
    this.clearError();

    if (!files.length) return;
    if (files.length > this.maxFiles - this.items.length) {
      this.showError(this.dataset.errorCount);
      return;
    }

    for (const file of files) {
      const error = this.validateFile(file);
      if (error) {
        this.showError(error);
        return;
      }
    }

    files.forEach((file) => {
      const position = this.getNextPosition();
      if (!position) return;

      this.items.push({
        id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
        file,
        position,
        preview: URL.createObjectURL(file),
      });
    });

    this.render();
  }

  validateFile(file) {
    const validTypes = ["image/jpeg", "image/png", "image/gif"];
    const validExtension = /\.(jpe?g|png|gif)$/i.test(file.name);

    if (!validTypes.includes(file.type) && !validExtension) {
      return this.dataset.errorType;
    }

    if (file.size > this.maxSize) {
      return this.dataset.errorSize;
    }

    return "";
  }

  getNextPosition() {
    return this.positions.find((position) => !this.items.some((item) => item.position === position));
  }

  onPositionChange(itemId, position) {
    const item = this.items.find((entry) => entry.id === itemId);
    if (!item) return;

    item.position = position;

    if (this.items.length === 2) {
      const other = this.items.find((entry) => entry.id !== itemId);
      if (other && other.position === position) {
        other.position = this.positions.find((entry) => entry !== position);
      }
    }

    this.render();
  }

  removeItem(itemId) {
    const item = this.items.find((entry) => entry.id === itemId);
    if (item) URL.revokeObjectURL(item.preview);
    this.items = this.items.filter((entry) => entry.id !== itemId);
    this.clearError();
    this.render();
  }

  render() {
    this.list.innerHTML = "";

    this.items.forEach((item) => {
      this.list.appendChild(this.createItemNode(item));
    });

    this.dropzone.hidden = this.items.length >= this.maxFiles;
    this.syncPropertyInputs();
  }

  createItemNode(item) {
    const li = document.createElement("li");
    li.className = "m-print-loader__file";

    const img = document.createElement("img");
    img.className = "m-print-loader__thumb";
    img.src = item.preview;
    img.alt = "";

    const meta = document.createElement("div");
    meta.className = "m-print-loader__meta";

    const name = document.createElement("span");
    name.className = "m-print-loader__file-name";
    name.textContent = item.file.name;

    const size = document.createElement("span");
    size.className = "m-print-loader__file-size";
    size.textContent = this.formatFileSize(item.file.size);

    meta.append(name, size);

    const select = document.createElement("select");
    select.className = "m-print-loader__position";
    select.setAttribute("aria-label", `Posição da imagem ${item.file.name}`);

    this.positions.forEach((position) => {
      const option = document.createElement("option");
      option.value = position;
      option.textContent = this.labels[position];
      option.selected = item.position === position;
      select.appendChild(option);
    });

    select.addEventListener("change", (event) => {
      this.onPositionChange(item.id, event.target.value);
    });

    const remove = document.createElement("button");
    remove.className = "m-print-loader__remove";
    remove.type = "button";
    remove.textContent = "Remover";
    remove.setAttribute("aria-label", `Remover ${item.file.name}`);
    remove.addEventListener("click", () => this.removeItem(item.id));

    li.append(img, meta, select, remove);
    return li;
  }

  syncPropertyInputs() {
    this.positions.forEach((position) => {
      const input = this.propertyInputs[position];
      if (!input) return;

      const item = this.items.find((entry) => entry.position === position);
      if (!item) {
        input.disabled = true;
        input.value = "";
        return;
      }

      const dataTransfer = new DataTransfer();
      dataTransfer.items.add(item.file);
      input.files = dataTransfer.files;
      input.disabled = false;
    });
  }

  validateBeforeSubmit() {
    this.clearError();

    if (this.items.length < 2) {
      this.syncPropertyInputs();
      return true;
    }

    const uniquePositions = new Set(this.items.map((item) => item.position));
    if (uniquePositions.size !== this.items.length) {
      this.showError(this.dataset.errorDuplicate);
      return false;
    }

    this.syncPropertyInputs();
    return true;
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  }

  showError(message) {
    this.error.textContent = message;
    this.error.hidden = false;
  }

  clearError() {
    this.error.textContent = "";
    this.error.hidden = true;
  }
}

if (!customElements.get("print-loader")) {
  customElements.define("print-loader", PrintLoader);
}
